/**
 * The three merge fields the certificate copy uses: recipient, KPI category,
 * and the report month. Nothing else appears on the certificate.
 */
export interface CertificateData {
  fullName: string;
  category: string;
  month: string;
}

/**
 * Signer shown on every certificate in a batch. Configured on the
 * Leaderboards page and held in memory only — never persisted.
 */
export interface CertificateSignature {
  /** PNG data URL rendered in place of the script signature text. */
  image?: string;
  /** Printed signature line, e.g. "Manny Rodriguez, CertainPath CEO". */
  name?: string;
}

export const DEFAULT_SIGNATURE_NAME = "Manny Rodriguez, CertainPath CEO";

const slugify = (value: string): string =>
  value
    .trim()
    // Drop punctuation (slashes, %, parens, apostrophes) while keeping letters
    // and digits from ANY script. `\w` is ASCII-only, so it used to strip the
    // accented/non-Latin letters in names like "José Peña" or "Müller"; the
    // Unicode letter/number classes (with the `u` flag) preserve them.
    .replace(/[^\p{L}\p{N}_\s-]/gu, "")
    .replace(/\s+/g, "-") // spaces → dashes
    .replace(/-+/g, "-") // collapse runs of dashes
    .replace(/^-|-$/g, ""); // trim leading/trailing dashes

/**
 * Download filename, e.g.
 * "Diana-Henderson-Call-Booking-Ratio-by-CCR-June-2026-certificate.pdf".
 */
export const certificateFileName = (data: CertificateData): string =>
  `${[slugify(data.fullName), slugify(data.category), slugify(data.month)]
    .filter(Boolean)
    .join("-")}-certificate.pdf`;

/** Filename for a multi-certificate bundle, e.g. "scoreboard-certificates-June-2026.pdf". */
export const certificatesBundleFileName = (month: string): string => {
  const monthSlug = slugify(month);
  return monthSlug
    ? `scoreboard-certificates-${monthSlug}.pdf`
    : "scoreboard-certificates.pdf";
};

/**
 * Stable, collision-free identity for a selectable certificate. A member can
 * appear in several categories, and two members with the same name can appear
 * in the same category for different companies — so identity is
 * (category, recipient, company), yielding one certificate each. JSON encoding
 * keeps the fields unambiguous regardless of the characters they contain.
 */
export const certificateKey = (
  category: string,
  fullName: string,
  company: string,
): string => JSON.stringify([category, fullName, company]);

/** Inverse of `certificateKey`. */
export const parseCertificateKey = (
  key: string,
): { category: string; fullName: string; company: string } => {
  const [category, fullName, company] = JSON.parse(key) as [
    string,
    string,
    string,
  ];
  return { category, fullName, company };
};
